import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { MessageSquare, Send, Bot, User, FileText, Loader2, Mic, MicOff } from 'lucide-react';
import { BrandLogo, brandColors } from '../components/Brand';
import api from '../api/api';
import { toast } from 'sonner';
import CreditConfirmDialog from '../components/CreditConfirmDialog';

const Chatbot = () => {
  const { t } = useTranslation();
  const { getAuthHeaders } = useAuth();
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [language, setLanguage] = useState('en');
  const [mode, setMode] = useState('Q&A');
  const [loading, setLoading] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [selectedDocId, setSelectedDocId] = useState('');
  const [isListening, setIsListening] = useState(false);
  const [creditDialogOpen, setCreditDialogOpen] = useState(false);
  const pendingQuestionRef = useRef('');
  const messagesEndRef = useRef(null);
  const recognitionRef = useRef(null);
  const committedTextRef = useRef('');

  useEffect(() => {
    document.title = 'ReasonDMS | AI Assistant';
    loadDocuments();
  }, []);

  const loadDocuments = async () => {
    try {
      const headers = getAuthHeaders();
      const response = await api.listDocuments({}, headers);
      setDocuments(response.data || []);
    } catch (error) {
      console.error('Error loading documents:', error);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Voice input using browser Speech Recognition
  const startListening = () => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      toast.error('Speech recognition is not supported in this browser. Please use Chrome or Edge.');
      return;
    }
    
    // Save current input as the committed base before voice starts
    committedTextRef.current = inputMessage;
    
    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = language === 'en' ? 'en-US' : language;
    
    recognition.onstart = () => {
      setIsListening(true);
      toast.info('Listening... Speak now');
    };
    
    recognition.onresult = (event) => {
      let finalTranscript = '';
      let interimTranscript = '';
      
      // Collect ALL results from the beginning of this recognition session
      for (let i = 0; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          finalTranscript += transcript;
        } else {
          interimTranscript += transcript;
        }
      }
      
      // Update committed text with any new final results
      if (finalTranscript) {
        const base = committedTextRef.current;
        committedTextRef.current = base ? `${base} ${finalTranscript}`.trim() : finalTranscript;
      }
      
      // Display: committed text + current interim preview
      const display = interimTranscript
        ? `${committedTextRef.current} ${interimTranscript}`.trim()
        : committedTextRef.current;
      
      setInputMessage(display);
    };
    
    recognition.onerror = (event) => {
      console.error('Speech recognition error:', event.error);
      if (event.error === 'not-allowed') {
        toast.error('Microphone access denied. Please allow microphone access in your browser settings.');
      } else if (event.error === 'no-speech') {
        toast.error('No speech detected. Please try again.');
      } else if (event.error !== 'aborted') {
        toast.error('Voice input error. Please try again.');
      }
      setIsListening(false);
    };
    
    recognition.onend = () => {
      setIsListening(false);
    };
    
    recognitionRef.current = recognition;
    recognition.start();
  };

  const stopListening = () => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      recognitionRef.current = null;
    }
    setIsListening(false);
  };

  const toggleVoice = () => {
    if (isListening) {
      stopListening();
    } else {
      startListening();
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
        recognitionRef.current = null;
      }
    };
  }, []);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!inputMessage.trim()) return;
    // Block-G: gate the AI query behind a credit confirmation dialog
    pendingQuestionRef.current = inputMessage;
    setCreditDialogOpen(true);
  };

  const runQuery = async () => {
    const question = pendingQuestionRef.current;
    if (!question || !question.trim()) return;

    const userMessage = {
      role: 'user',
      content: question,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setLoading(true);

    try {
      const headers = getAuthHeaders();
      const payload = {
        mode,
        question,
        language,
        top_k: 5
      };
      
      // Add selected document for strict context binding
      if (selectedDocId) {
        payload.selected_doc_id = selectedDocId;
      }
      
      const response = await api.askQuestion(payload, headers);

      const assistantMessage = {
        role: 'assistant',
        content: response.data.answer,
        citations: response.data.citations,
        docs_consulted: response.data.docs_consulted,
        confidence: response.data.confidence,
        timestamp: new Date()
      };

      setMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      console.error('Error asking question:', error);
      toast.error('Failed to get response from ReasonDMS AI');
      
      const errorMessage = {
        role: 'assistant',
        content: 'I apologize, but I encountered an error processing your request. Please try again.',
        timestamp: new Date(),
        error: true
      };
      
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  const languages = [
    { value: 'en', label: 'English' },
    { value: 'hi', label: 'हिंदी' },
    { value: 'bn', label: 'বাংলা' },
    { value: 'ta', label: 'தமிழ்' },
    { value: 'te', label: 'తెలుగు' },
    { value: 'mr', label: 'मराठी' },
    { value: 'gu', label: 'ગુજરાતી' },
    { value: 'kn', label: 'ಕನ್ನಡ' },
    { value: 'ml', label: 'മലയാളം' },
    { value: 'pa', label: 'ਪੰਜਾਬੀ' }
  ];

  const modes = [
    { value: 'Q&A', label: t('chatbot.modes.qa') },
    { value: 'Summarise', label: t('chatbot.modes.summarise') },
    { value: 'Compare', label: t('chatbot.modes.compare') },
    { value: 'Extract Fields', label: t('chatbot.modes.extract') }
  ];

  return (
    <div data-testid="chatbot-page" className="h-full flex flex-col">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold" style={{ color: brandColors.primary }}>
              ReasonDMS AI Assistant
            </h1>
            <p className="text-slate-600 mt-1">{t('chatbot.placeholder')}</p>
          </div>
          <div className="flex gap-3">
            <Select value={language} onValueChange={setLanguage}>
              <SelectTrigger className="w-40" data-testid="language-selector">
                <SelectValue placeholder={t('chatbot.selectLanguage')} />
              </SelectTrigger>
              <SelectContent>
                {languages.map(lang => (
                  <SelectItem key={lang.value} value={lang.value}>
                    {lang.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={mode} onValueChange={setMode}>
              <SelectTrigger className="w-48" data-testid="mode-selector">
                <SelectValue placeholder={t('chatbot.selectMode')} />
              </SelectTrigger>
              <SelectContent>
                {modes.map(m => (
                  <SelectItem key={m.value} value={m.value}>
                    {m.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={selectedDocId || "all"} onValueChange={(val) => setSelectedDocId(val === "all" ? "" : val)}>
              <SelectTrigger className="w-56" data-testid="doc-context-selector">
                <SelectValue placeholder="All Documents" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Documents</SelectItem>
                {documents.map(doc => (
                  <SelectItem key={doc.id} value={doc.id}>
                    {doc.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Chat Area */}
      <Card className="flex-1 flex flex-col overflow-hidden">
        <CardHeader className="border-b" style={{ backgroundColor: '#f8fafc' }}>
          <CardTitle className="flex items-center gap-3">
            <div className="p-1.5 rounded-lg" style={{ backgroundColor: brandColors.primary }}>
              <MessageSquare className="w-5 h-5 text-white" />
            </div>
            <span style={{ color: brandColors.primary }}>
              ReasonDMS Chat
            </span>
            <span className="text-sm font-normal text-slate-500">
              ({modes.find(m => m.value === mode)?.label || 'Q&A'})
              {selectedDocId && (
                <span className="ml-2 px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs">
                  Context: {documents.find(d => d.id === selectedDocId)?.title || 'Selected Document'}
                </span>
              )}
            </span>
          </CardTitle>
        </CardHeader>
        
        <CardContent className="flex-1 overflow-y-auto p-6 space-y-4">
          {messages.length === 0 ? (
            <div className="h-full flex items-center justify-center">
              <div className="text-center max-w-md">
                <div className="mb-6 flex justify-center">
                  <div className="p-4 rounded-2xl" style={{ backgroundColor: '#e8f4fd' }}>
                    <BrandLogo size="large" />
                  </div>
                </div>
                <h3 className="text-xl font-semibold mb-2" style={{ color: brandColors.primary }}>
                  Welcome to ReasonDMS AI Assistant
                </h3>
                <p className="text-slate-600 mb-4">
                  I can help you securely search and understand your authorized documents. 
                  Ask me to find information, summarize content, compare documents, or extract key fields.
                </p>
                <div className="text-sm text-slate-500 space-y-2 bg-slate-50 p-4 rounded-lg">
                  <p className="font-medium" style={{ color: brandColors.accent }}>Try asking:</p>
                  <p>&quot;What are the key terms in the latest contract?&quot;</p>
                  <p>&quot;Summarize all invoices from last month&quot;</p>
                  <p>&quot;Compare version 1 and version 2 of the policy&quot;</p>
                </div>
              </div>
            </div>
          ) : (
            messages.map((message, index) => (
              <div
                key={index}
                data-testid={`message-${index}`}
                className={`flex gap-3 ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                {message.role === 'assistant' && (
                  <div 
                    className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: '#e8f4fd' }}
                  >
                    <Bot className="w-5 h-5" style={{ color: brandColors.primary }} />
                  </div>
                )}
                
                <div className={`max-w-3xl ${message.role === 'user' ? 'order-first' : ''}`}>
                  <div
                    className={`p-4 rounded-lg ${
                      message.role === 'user'
                        ? 'text-white'
                        : message.error
                        ? 'bg-red-50 text-red-900 border border-red-200'
                        : 'bg-slate-100 text-slate-900'
                    }`}
                    style={message.role === 'user' ? { backgroundColor: brandColors.primary } : {}}
                  >
                    <p className="whitespace-pre-wrap">{message.content}</p>
                  </div>

                  {message.role === 'assistant' && !message.error && (
                    <div className="mt-2 space-y-2">
                      {message.confidence !== undefined && (
                        <p className="text-xs text-slate-500">
                          {t('chatbot.confidence')}: {Math.round(message.confidence * 100)}%
                        </p>
                      )}
                      
                      {message.citations && message.citations.length > 0 && (
                        <div className="bg-slate-50 p-3 rounded border border-slate-200">
                          <p className="text-xs font-semibold text-slate-700 mb-2">{t('chatbot.citations')}:</p>
                          <div className="space-y-1">
                            {message.citations.map((citation, idx) => (
                              <div key={idx} className="flex items-center gap-2 text-xs">
                                <FileText className="w-3 h-3" style={{ color: brandColors.primary }} />
                                <span className="text-slate-700">{citation.doc_title}</span>
                                <span className="text-slate-500">v{citation.version}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  <p className="text-xs text-slate-400 mt-1">
                    {message.timestamp.toLocaleTimeString()}
                  </p>
                </div>

                {message.role === 'user' && (
                  <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center flex-shrink-0">
                    <User className="w-5 h-5 text-slate-600" />
                  </div>
                )}
              </div>
            ))
          )}

          {loading && (
            <div className="flex gap-3">
              <div 
                className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: '#e8f4fd' }}
              >
                <Bot className="w-5 h-5" style={{ color: brandColors.primary }} />
              </div>
              <div className="bg-slate-100 p-4 rounded-lg">
                <Loader2 className="w-5 h-5 animate-spin" style={{ color: brandColors.primary }} />
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </CardContent>

        {/* Input Area */}
        <div className="p-4 border-t bg-white">
          <form onSubmit={handleSendMessage} className="flex gap-3">
            <Input
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              placeholder={isListening ? 'Listening... speak now' : t('chatbot.placeholder')}
              data-testid="chat-input"
              className={`flex-1 ${isListening ? 'border-red-300 bg-red-50' : ''}`}
              disabled={loading}
            />
            <Button
              type="button"
              variant={isListening ? 'destructive' : 'outline'}
              onClick={toggleVoice}
              title={isListening ? 'Click to stop listening' : 'Click to speak'}
              data-testid="voice-input-toggle"
              disabled={loading}
            >
              {isListening ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
            </Button>
            <Button 
              type="submit" 
              disabled={loading || !inputMessage.trim()}
              data-testid="send-button"
              style={{ backgroundColor: brandColors.primary }}
            >
              <Send className="w-4 h-4" />
            </Button>
          </form>
          <p className="text-xs text-slate-400 mt-2 text-center">
            ReasonDMS AI only searches documents you have permission to access.
            {isListening && (
              <span className="text-red-500 ml-1 inline-flex items-center gap-1">
                <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse inline-block"></span>
                Listening... Click mic to stop
              </span>
            )}
          </p>
        </div>
      </Card>

      <CreditConfirmDialog
        open={creditDialogOpen}
        onOpenChange={setCreditDialogOpen}
        actionKey="ai_qa"
        title="Confirm AI Chat Query"
        onConfirm={runQuery}
      />
    </div>
  );
};

export default Chatbot;
